import { useNamedHeader } from 'hooks/useNamedHeader';
import React, { useCallback, useContext, useEffect, useReducer } from 'react';
import { editVideoCategoryReducer } from './editVideoCategoryReducer';
import {
	EditVideoCategoryContext,
	editVideoCategoryInitialState,
	TEditVideoCategoryVisibleForm,
	TTypeOfEditVideoCategoryModalWindow,
} from './editVideoCategoryContext';
import { EditVideoCategoryActionTypes } from './editVideoCategoryActions';
import {
	IItemForGetShortInfo,
	TLanguageIDs,
	IUpdateVideoCategory,
	IVideoCategoryTranslation,
	IGetVideoCategoryInfo,
} from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { getVideoCategoryInfo } from 'backend/videos';
import { AppContext, IAppContext } from 'appState/appContext';

const EditVideoCategoryState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(editVideoCategoryReducer, editVideoCategoryInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);

	const params = new URL(document.location.href).searchParams;
	const categoryID = params.get('categoryID');

	const { response, loading, refetch } = useFetchData<IGetVideoCategoryInfo, string>(
		categoryID ? getVideoCategoryInfo : null,
		categoryID || '',
	);

	useEffect(() => {
		if (state.needRefetchData) {
			refetch();
			dispatch({
				type: EditVideoCategoryActionTypes.REFETCH_DATA,
				payload: {
					needRefetch: false,
				},
			});
		}
	}, [state.needRefetchData]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useNamedHeader(response?.data.videoCategory.name);

	useEffect(() => {
		if (response) {
			dispatch({
				type: EditVideoCategoryActionTypes.SET_ALL_DATA,
				payload: {
					categoryID: response.data.videoCategory.id,
					name: response.data.videoCategory.name || '',
					IDsSelectedLanguages: response.data.videoCategory.translations.map((el) => el.languageId),
					translations: response.data.videoCategory.translations,
				},
			});
			dispatch({
				type: EditVideoCategoryActionTypes.SET_INITIAL_FORM_DATA,
				payload: {
					initialFormData: {
						name: response.data.videoCategory.name || '',
						IDsSelectedLanguages: response.data.videoCategory.translations.map((el) => el.languageId),
						translations: response.data.videoCategory.translations,
					},
				},
			});
		}
	}, [response]);

	const refetchData = useCallback((value: boolean) => {
		dispatch({
			type: EditVideoCategoryActionTypes.REFETCH_DATA,
			payload: {
				needRefetch: value,
			},
		});
	}, []);

	const changeVisibleForm = useCallback((value: TEditVideoCategoryVisibleForm) => {
		dispatch({
			type: EditVideoCategoryActionTypes.CHANGE_VISIBLE_FORM,
			payload: {
				visibleForm: value,
			},
		});
	}, []);

	const changeName = useCallback((value: string) => {
		dispatch({
			type: EditVideoCategoryActionTypes.CHANGE_NAME,
			payload: {
				name: value,
			},
		});
	}, []);

	const setFetchedLanguages = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditVideoCategoryActionTypes.SET_FETCHED_LANGUAGES,
			payload: {
				languages: value,
			},
		});
	}, []);

	const setIDsLanguages = useCallback(
		(value: TLanguageIDs[]) => {
			dispatch({
				type: EditVideoCategoryActionTypes.SET_IDs_LANGUAGES,
				payload: {
					IDsLanguages: value,
				},
			});

			const isNewLanguageAdded = value.length > state.translations.length;
			if (isNewLanguageAdded) {
				const existingTranslationIDs = state.translations.map((el) => el.languageId);
				const newTranslationID = value.find((el) => !existingTranslationIDs.includes(el));
				if (newTranslationID) {
					const newTranslation: IVideoCategoryTranslation = {
						languageId: newTranslationID,
						name: '',
					};
					const updatedTranslations = [...state.translations, newTranslation];
					dispatch({
						type: EditVideoCategoryActionTypes.SET_TRANSLATION_FIELDS,
						payload: {
							updatedTranslations,
						},
					});
				}
			} else {
				const updatedTranslations = state.translations.filter((el) => value.includes(el.languageId));
				dispatch({
					type: EditVideoCategoryActionTypes.SET_TRANSLATION_FIELDS,
					payload: {
						updatedTranslations,
					},
				});
			}
		},
		[state.translations],
	);

	const setDataForSubmit = useCallback((value: IUpdateVideoCategory | null) => {
		dispatch({
			type: EditVideoCategoryActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				data: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfEditVideoCategoryModalWindow) => {
		dispatch({
			type: EditVideoCategoryActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const setTranslationTab = useCallback((value: TLanguageIDs | null) => {
		dispatch({
			type: EditVideoCategoryActionTypes.SET_TRANSLATION_TAB,
			payload: {
				translationTab: value,
			},
		});
	}, []);

	const changeNameTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							name: value,
					  }
					: translation,
			);
			dispatch({
				type: EditVideoCategoryActionTypes.CHANGE_TRANSLATION,
				payload: {
					updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const resetFields = useCallback(() => dispatch({ type: EditVideoCategoryActionTypes.RESET_FIELDS }), []);

	return (
		<EditVideoCategoryContext.Provider
			value={{
				refetchData,
				changeVisibleForm,
				changeName,
				setFetchedLanguages,
				setIDsLanguages,
				setDataForSubmit,
				changeTypeOfModalWindow,
				setTranslationTab,
				changeNameTranslation,
				resetFields,
				needRefetchData: state.needRefetchData,
				initialFormData: state.initialFormData,
				categoryID: state.categoryID,
				visibleForm: state.visibleForm,
				name: state.name,
				fetchedLanguages: state.fetchedLanguages,
				IDsSelectedLanguages: state.IDsSelectedLanguages,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
				selectedTranslationTab: state.selectedTranslationTab,
				translations: state.translations,
			}}>
			{children}
		</EditVideoCategoryContext.Provider>
	);
};

export default EditVideoCategoryState;
